// noinspection DuplicatedCode

import color from 'color';
import { DefaultTheme } from 'styled-components';

/* eslint-disable @typescript-eslint/no-unused-vars */
// Background colors
const colorBackground = color.rgb(0, 0, 0);
const colorElevation0 = colorBackground.lightness(6);
const colorElevation1 = colorBackground.lightness(7);
const colorElevation2 = colorBackground.lightness(9);
const colorElevation3 = colorBackground.lightness(11);
const colorElevation4 = colorBackground.lightness(13);
const colorElevation5 = colorBackground.lightness(15);
const colorElevation6 = colorBackground.lightness(17);
const colorElevation7 = colorBackground.lightness(19);
const colorElevation8 = colorBackground.lightness(23);
const colorElevation9 = colorBackground.lightness(25);

// Text colors
const colorText = color.rgb(255, 255, 255);
const colorEmphasisHigh = colorText.alpha(0.87);
const colorEmphasisMedium = colorText.alpha(0.64);
const colorEmphasisLow = colorText.alpha(0.3);
const colorInverted = color.rgb(0, 0, 0);

// Miscellaneous colors
const colorHighlight = color.rgb(255, 255, 0).darken(0.2);
const colorInfo = color.rgb(94, 96, 206);
const colorNegative = color.rgb(229, 56, 59);
const colorPositive = color.rgb(144, 169, 85);
const colorPrimary = color.rgb(0, 119, 182);
const colorWarning = color.rgb(247, 127, 0);

// Transaction type colors
const colorTxBank = color.rgb(60, 150, 110);
const colorTxState = color.rgb(155, 150, 140);
const colorTxSystem = color.rgb(0, 162, 215).darken(0.2);

// Transaction state colors
const colorTxIgnored = color.rgb(244, 140, 6);
const colorTxMatched = color.rgb(171, 184, 84);
const colorTxUnmatched = colorEmphasisLow;

// Transaction action and highlight colors
const colorTxDeselect = color.rgb(205, 180, 219);
const colorTxHighlight = color.rgb(255, 109, 0);
const colorTxIgnore = colorTxIgnored;
const colorTxMatch = colorTxMatched.darken(0.3);
const colorTxRestore = color.rgb(0, 180, 216);
const colorTxSuccess = colorPositive;
const colorTxUnmatch = color.rgb(255, 0, 0).lighten(0.2);
/* eslint-enable @typescript-eslint/no-unused-vars */

export const dark: DefaultTheme = {
  body: {
    background: colorElevation1.hex(),
    color: colorEmphasisHigh.toString(),
  },
  brand: {
    main: colorPrimary.toString(),
  },
  breadcrumbs: {
    border: colorElevation6.toString(),
    chevron: colorEmphasisMedium.toString(),
    highlight: colorPrimary.alpha(0.5).toString(),
    icon: colorEmphasisMedium.toString(),
  },
  button: {
    default: {
      background: colorElevation5.toString(),
      color: colorEmphasisHigh.toString(),
      disabled: {
        background: colorElevation5.alpha(0.5).toString(),
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorElevation8.toString(),
      },
    },
    negative: {
      background: colorNegative.toString(),
      color: colorEmphasisHigh.toString(),
      disabled: {
        background: colorNegative.alpha(0.5).toString(),
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorNegative.darken(0.15).toString(),
      },
    },
    positive: {
      background: colorPositive.toString(),
      color: colorEmphasisHigh.toString(),
      disabled: {
        background: colorPositive.alpha(0.5).toString(),
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorPositive.darken(0.15).toString(),
      },
    },
    primary: {
      background: colorPrimary.toString(),
      color: colorEmphasisHigh.toString(),
      disabled: {
        background: colorPrimary.alpha(0.5).toString(),
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorPrimary.darken(0.15).toString(),
      },
    },
    transparent: {
      background: 'transparent',
      color: colorEmphasisHigh.toString(),
      disabled: {
        background: 'transparent',
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorElevation7.toString(),
      },
    },
  },
  card: {
    actions: {
      background: colorElevation2.toString(),
    },
    background: colorElevation2.toString(),
    header: {
      border: colorElevation5.toString(),
    },
    meta: {
      color: colorEmphasisLow.toString(),
    },
    progress: {
      background: colorElevation1.toString(),
      color: colorPrimary.toString(),
    },
    status: {
      background: colorElevation0.toString(),
      color: colorEmphasisMedium.toString(),
      item: {
        background: 'transparent',
        hover: {
          background: colorElevation2.toString(),
        },
      },
    },
  },
  datePicker: {
    calendar: {
      button: {
        color: colorEmphasisHigh.toString(),
        disabled: {
          background: colorElevation4.toString(),
          color: colorEmphasisLow.toString(),
        },
        hover: {
          background: colorElevation4.toString(),
        },
      },
      day: {
        hover: {
          border: colorElevation9.toString(),
        },
      },
      neighboringMonth: {
        color: colorEmphasisLow.toString(),
      },
      selected: {
        background: colorPrimary.toString(),
        border: colorPrimary.toString(),
        color: colorEmphasisHigh.toString(),
      },
      today: {
        border: colorPrimary.toString(),
        color: colorPrimary.toString(),
      },
      weekend: {
        color: colorNegative.lighten(0.5).toString(),
      },
    },
    icon: {
      color: colorEmphasisLow.toString(),
      hover: colorEmphasisMedium.toString(),
    },
    input: {
      invalid: {
        background: colorNegative.alpha(0.3).toString(),
      },
    },
  },
  element: {
    code: {
      background: colorElevation1.toString(),
    },
    kbd: {
      background: colorElevation8.toString(),
      shadow: '0 0.25em 0 0 rgba(0, 0, 0, 0.5)',
    },
    height: 42,
    lineHeight: '1.4em',
  },
  form: {
    error: colorNegative.toString(),
  },
  fullscreen: {
    background: colorElevation1.toString(),
    panel: {
      background: colorElevation2.toString(),
    },
  },
  highlight: {
    background: colorHighlight.toString(),
    color: colorInverted.toString(),
  },
  input: {
    background: colorElevation1.toString(),
    border: colorElevation8.toString(),
    color: colorEmphasisHigh.toString(),
    focus: {
      border: colorPrimary.toString(),
    },
    placeholder: {
      color: colorEmphasisLow.toString(),
    },
  },
  link: {
    color: colorPrimary.toString(),
    hover: {
      color: colorPrimary.darken(0.2).toString(),
    },
  },
  menuButton: {
    background: colorElevation1.toString(),
    border: colorElevation4.toString(),
    item: {
      disabled: {
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorElevation4.toString(),
        color: colorEmphasisHigh.toString(),
      },
    },
    separator: {
      color: colorElevation4.toString(),
    },
  },
  notification: {
    background: colorElevation4.toString(),
    caption: {
      color: colorEmphasisHigh.toString(),
    },
    color: colorEmphasisMedium.toString(),
  },
  placeholder: {
    base: colorElevation3.toString(),
    highlight: colorElevation1.toString(),
  },
  radius: {
    tiny: '0.15rem',
    small: '0.25rem',
    medium: '0.5rem',
    large: '0.8rem',
  },
  scrollbar: {
    thumb: {
      background: colorElevation9.toString(),
      hover: colorElevation9.darken(0.3).toString(),
      size: 12,
    },
    track: {
      background: colorElevation3.toString(),
      spacing: 2,
    },
  },
  select: {
    background: colorElevation1.toString(),
    border: colorElevation8.toString(),
    color: colorEmphasisHigh.toString(),
    focus: {
      border: colorPrimary.toString(),
    },
    icon: colorEmphasisLow.toString(),
    option: {
      background: colorElevation1.toString(),
      border: colorElevation4.toString(),
      selected: {
        background: colorElevation4.toString(),
      },
    },
    placeholder: {
      color: colorEmphasisLow.toString(),
    },
  },
  selectList: {
    option: {
      selected: {
        background: colorPrimary.darken(0.5).toString(),
        color: colorEmphasisHigh.toString(),
      },
    },
  },
  selection: {
    background: colorPrimary.toString(),
    color: colorEmphasisHigh.toString(),
  },
  severity: {
    info: colorInfo.toString(),
    negative: colorNegative.toString(),
    none: colorEmphasisLow.toString(),
    positive: colorPositive.toString(),
    primary: colorPrimary.toString(),
    warning: colorWarning.toString(),
  },
  shadow: {
    highlight: [
      '0 0 0.2px rgba(0, 0, 0, 0.037)',
      '0 0 0.4px rgba(0, 0, 0, 0.053)',
      '0 0 0.9px rgba(0, 0, 0, 0.067)',
      '0 0 1.8px rgba(0, 0, 0, 0.083)',
      '0 0 5px rgba(0, 0, 0, 0.12)',
    ].join(','),
    small: [
      '0 0.2px 0.2px rgba(0, 0, 0, 0.037)',
      '0 0.4px 0.5px rgba(0, 0, 0, 0.053)',
      '0 0.9px 1.1px rgba(0, 0, 0, 0.067)',
      '0 1.8px 2.2px rgba(0, 0, 0, 0.083)',
      '0 5px 6px rgba(0, 0, 0, 0.12)',
    ].join(','),
    medium: [
      '0 0.3px 0.3px rgba(0, 0, 0, 0.037)',
      '0 0.9px 0.8px rgba(0, 0, 0, 0.053)',
      '0 1.8px 1.6px rgba(0, 0, 0, 0.067)',
      '0 3.7px 3.3px rgba(0, 0, 0, 0.083)',
      '0 10px 9px rgba(0, 0, 0, 0.12)',
    ].join(','),
    large: [
      '0 0.3px 0.2px rgba(0, 0, 0, 0.047)',
      '0 0.7px 0.5px rgba(0, 0, 0, 0.067)',
      '0 1.4px 1.1px rgba(0, 0, 0, 0.083)',
      '0 2.9px 2.2px rgba(0, 0, 0, 0.103)',
      '0 8px 6px rgba(0, 0, 0, 0.15)',
    ].join(','),
    huge: [
      '0 0 0.4px rgba(0, 0, 0, 0.056)',
      '0 0 1px rgba(0, 0, 0, 0.08)',
      '0 0 2.1px rgba(0, 0, 0, 0.1)',
      '0 0 4.4px rgba(0, 0, 0, 0.124)',
      '0 0 12px rgba(0, 0, 0, 0.18)',
    ].join(','),
  },
  spacing: {
    absolute: {
      tiny: '0.25rem',
      small: '0.5rem',
      smaller: '0.75rem',
      medium: '1rem',
      larger: '1.25rem',
      large: '1.5rem',
      huge: '2rem',
    },
    relative: {
      tiny: '0.25em',
      small: '0.5em',
      smaller: '0.75em',
      medium: '1em',
      larger: '1.25em',
      large: '1.5em',
      huge: '2em',
    },
  },
  table: {
    border: colorElevation4.toString(),
    details: {
      background: colorElevation3.toString(),
    },
    header: {
      color: colorEmphasisLow.toString(),
    },
    highlight: {
      background: colorPrimary.alpha(0.15).toString(),
    },
    hover: {
      background: colorElevation4.toString(),
      color: colorEmphasisHigh.toString(),
    },
    select: {
      background: colorPrimary.darken(0.5).toString(),
      color: colorEmphasisHigh.toString(),
    },
  },
  timeline: {
    icon: {
      background: colorPositive.toString(),
      color: colorEmphasisHigh.toString(),
    },
    line: {
      color: colorElevation6.toString(),
    },
    time: {
      color: colorEmphasisLow.toString(),
    },
  },
  transaction: {
    action: {
      archive: colorTxIgnore.toString(),
      disabled: {
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorElevation4.toString(),
      },
      match: colorTxMatch,
      restore: colorTxRestore.toString(),
      select: colorTxDeselect.toString(),
      unmatch: colorTxUnmatch.toString(),
    },
    bank: {
      color: colorTxBank,
    },
    error: colorNegative,
    info: {
      highlight: colorTxHighlight.toString(),
    },
    label: {
      color: colorInverted.toString(),
    },
    match: {
      color: colorTxState,
    },
    status: {
      archived: colorTxIgnored.toString(),
      matched: colorTxMatched.toString(),
      unmatched: colorTxUnmatched.toString(),
    },
    success: colorTxSuccess,
    system: {
      color: colorTxSystem,
    },
  },
  transition: {
    fast: '100ms',
    medium: '150ms',
    slow: '300ms',
  },
  typography: {
    emphasis: {
      high: colorEmphasisHigh.toString(),
      medium: colorEmphasisMedium.toString(),
      low: colorEmphasisLow.toString(),
    },
  },
};
